/**
 * Browser SSE Client for Story Creation Sessions
 *
 * Uses fetch API with ReadableStream to handle Server-Sent Events,
 * adapted for browser environment from the mobile sessionStreamClient.ts.
 *
 * Features:
 * - POST requests with body (unlike native EventSource which is GET-only)
 * - Automatic authentication header injection
 * - Stream parsing with typed event callbacks
 * - Connection lifecycle management with abort support
 */

import { PIXIEWORLD_API_URI } from '@/shared/consts'

import type { SlidePreview } from '@/shared/api/models'

// Re-export for convenience
export type { SlidePreview }

// SSE event types
export interface SSEEvent {
  event: string
  data: string
  id?: string
}

export interface SSEConnection {
  close: () => void
}

export interface SSECallbacks {
  onOpen?: () => void
  onMessage?: (event: SSEEvent) => void
  onError?: (error: Error) => void
  onEvent?: (eventType: string, data: unknown) => void
  onClose?: () => void
}

/**
 * Typed SSE events for plot refinement
 */
export interface PlotRefinementEvents {
  status: { message: string }
  token: { content: string }
  complete: { plot: PlotData }
  error: { message: string }
}

export interface PlotData {
  title: string
  themes: string[]
  quick_summary: string
  plot_narrative: string
  moral_or_lesson?: string
}

/**
 * Typed SSE events for slide generation
 */
export interface SlideGenerationEvents {
  status: { message: string }
  slide: { slide: SlidePreview; index: number; total: number }
  complete: { total_slides: number; message: string }
  error: { message: string }
}

/**
 * Parse SSE formatted text into events
 * SSE format: "event: type\ndata: {...}\n\n"
 */
function parseSSEChunk(chunk: string): SSEEvent[] {
  const events: SSEEvent[] = []
  const lines = chunk.split('\n')

  let currentEvent: Partial<SSEEvent> = {}

  for (const line of lines) {
    if (line.startsWith('event:')) {
      currentEvent.event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      currentEvent.data = line.slice(5).trim()
    } else if (line.startsWith('id:')) {
      currentEvent.id = line.slice(3).trim()
    } else if (line === '' && currentEvent.event && currentEvent.data !== undefined) {
      // Empty line marks end of event
      events.push(currentEvent as SSEEvent)
      currentEvent = {}
    }
  }

  return events
}

/**
 * Parse event data as JSON if possible
 */
function parseEventData(data: string): unknown {
  try {
    if (data.startsWith('{') || data.startsWith('[')) {
      return JSON.parse(data)
    }
  } catch {
    // Return as-is if not valid JSON
  }

  return data
}

/**
 * Create an SSE connection using fetch with ReadableStream
 *
 * @param endpoint - The SSE endpoint path (e.g., '/api/sessions/{uuid}/chat/stream/')
 * @param body - Request body to send with POST
 * @param accessToken - JWT access token for authentication
 * @param callbacks - Event callbacks for handling stream events
 * @returns SSEConnection with close() method
 */
export async function createSSEConnection(
  endpoint: string,
  body: object,
  accessToken: string,
  callbacks: SSECallbacks,
): Promise<SSEConnection> {
  const abortController = new AbortController()

  const url = `${PIXIEWORLD_API_URI.replace(/\/$/, '')}${endpoint}`

  // Start the fetch request
  const fetchPromise = fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: abortController.signal,
  })

  // Process the stream in a separate async function
  const processStream = async () => {
    try {
      const response = await fetchPromise

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`)
      }

      if (!response.body) {
        throw new Error('Response body is null')
      }

      callbacks.onOpen?.()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          // Process any remaining buffer content
          if (buffer.trim()) {
            const events = parseSSEChunk(buffer)

            for (const event of events) {
              callbacks.onMessage?.(event)

              const parsedData = parseEventData(event.data)

              callbacks.onEvent?.(event.event, parsedData)
            }
          }

          callbacks.onClose?.()
          break
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true })

        // Process complete events (separated by double newlines)
        const eventSeparator = '\n\n'
        let separatorIndex: number

        while ((separatorIndex = buffer.indexOf(eventSeparator)) !== -1) {
          const eventText = buffer.slice(0, separatorIndex)

          buffer = buffer.slice(separatorIndex + eventSeparator.length)

          const events = parseSSEChunk(eventText + '\n\n')

          for (const event of events) {
            callbacks.onMessage?.(event)

            const parsedData = parseEventData(event.data)

            callbacks.onEvent?.(event.event, parsedData)
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Connection was intentionally closed
        callbacks.onClose?.()

        return
      }

      callbacks.onError?.(error instanceof Error ? error : new Error(String(error)))
    }
  }

  // Start processing the stream
  processStream()

  // Return connection interface
  return {
    close: () => {
      abortController.abort()
    },
  }
}

/**
 * Helper to create plot refinement SSE connection
 */
export async function createPlotRefinementConnection(
  sessionUuid: string,
  message: string,
  accessToken: string,
  callbacks: {
    onToken?: (content: string) => void
    onComplete?: (plot: PlotData) => void
    onError?: (error: Error) => void
    onClose?: () => void
  },
): Promise<SSEConnection> {
  return createSSEConnection(
    `/api/sessions/${sessionUuid}/chat/stream/`,
    { message },
    accessToken,
    {
      onOpen: () => {
        // Connection opened
      },
      onEvent: (eventType, data) => {
        switch (eventType) {
          case 'token': {
            const tokenData = data as PlotRefinementEvents['token']

            callbacks.onToken?.(tokenData.content)
            break
          }

          case 'complete': {
            const completeData = data as PlotRefinementEvents['complete']

            callbacks.onComplete?.(completeData.plot)
            break
          }

          case 'error': {
            const errorData = data as PlotRefinementEvents['error']

            callbacks.onError?.(new Error(errorData.message))
            break
          }
        }
      },
      onError: callbacks.onError,
      onClose: callbacks.onClose,
    },
  )
}

/**
 * Helper to create slide generation SSE connection
 */
export async function createSlideGenerationConnection(
  sessionUuid: string,
  accessToken: string,
  callbacks: {
    onStatus?: (message: string) => void
    onSlide?: (slide: SlidePreview, index: number, total: number) => void
    onComplete?: (totalSlides: number) => void
    onError?: (error: Error) => void
    onClose?: () => void
  },
): Promise<SSEConnection> {
  return createSSEConnection(
    `/api/sessions/${sessionUuid}/slides/stream/`,
    {},
    accessToken,
    {
      onOpen: () => {
        // Connection opened
      },
      onEvent: (eventType, data) => {
        switch (eventType) {
          case 'status': {
            const statusData = data as SlideGenerationEvents['status']

            callbacks.onStatus?.(statusData.message)
            break
          }

          case 'slide': {
            const slideData = data as SlideGenerationEvents['slide']

            callbacks.onSlide?.(slideData.slide, slideData.index, slideData.total)
            break
          }

          case 'complete': {
            const completeData = data as SlideGenerationEvents['complete']

            callbacks.onComplete?.(completeData.total_slides)
            break
          }

          case 'error': {
            const errorData = data as SlideGenerationEvents['error']

            callbacks.onError?.(new Error(errorData.message))
            break
          }
        }
      },
      onError: callbacks.onError,
      onClose: callbacks.onClose,
    },
  )
}
